import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { EventInput } from "@fullcalendar/core";
import plLocale from '@fullcalendar/core/locales/pl';
import { useAppointmentsApi } from "../hooks/useAppointmentsApi";
import { useHairdressersApi } from "../hooks/useHairdressersApi";
import { useServiceTemplatesApi } from "../hooks/useServiceTemplatesApi";
import { useClientsApi } from "../hooks/useClientsApi";
import { useTimeoffsApi } from "../hooks/useTimeoffsApi";
import { Appointment, AppointmentStatus, Hairdresser, Client, ServiceTemplate, Service, Timeoff } from "../types/entities";
import { AppointmentFormData, AppointmentServiceFormData, ClientFormData } from "../types/forms";
import AppointmentInfoDialog from "../components/AppointmentInfoDialog";
import AppointmentFormModal from "../components/AppointmentFormModal";
import AppointmentDeleteConfirmationDialog from "../components/AppointmentDeleteConfirmationDialog";
import AppointmentContextMenu from "../components/AppointmentContextMenu";
import ClientFormModal from "../components/ClientFormModal";

// Custom styles for FullCalendar header buttons and drag-and-drop
const calendarHeaderStyles = `
  /* Drag and drop visual feedback */
  .fc-event-dragging {
    opacity: 0.8 !important;
    transform: rotate(2deg) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    z-index: 1000 !important;
  }
  
  .fc-event-resizing {
    opacity: 0.8 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
  
  /* Cursor styles for draggable events */
  .fc-event[data-editable="true"] {
    cursor: move !important;
  }
  
  .fc-event[data-editable="true"]:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-1px) !important;
    transition: all 0.2s ease !important;
  }
  
  /* Resize handles */
  .fc-event[data-duration-editable="true"] .fc-event-resizer {
    cursor: ns-resize !important;
    opacity: 0.7 !important;
  }
  
  .fc-event[data-duration-editable="true"] .fc-event-resizer:hover {
    opacity: 1 !important;
    background-color: rgba(255, 255, 255, 0.3) !important;
  }
  
  /* Disabled state for completed/canceled appointments */
  .fc-event[data-editable="false"] {
    cursor: default !important;
    opacity: 0.7 !important;
  }
  
  .fc-event[data-editable="false"]:hover {
    transform: none !important;
    box-shadow: none !important;
  }
  
  /* Visual indicator for editable vs non-editable */
  .fc-event[data-editable="true"]::after {
    content: '⋮⋮' !important;
    position: absolute !important;
    right: 4px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    font-size: 8px !important;
    color: rgba(255, 255, 255, 0.6) !important;
    line-height: 1 !important;
    letter-spacing: 1px !important;
  }
  
  /* Loading state during drag operations */
  .fc-event-dragging .fc-event-title::after {
    content: ' (Moving...)' !important;
    font-style: italic !important;
    opacity: 0.8 !important;
  }
  
  .fc-event-resizing .fc-event-title::after {
    content: ' (Resizing...)' !important;
    font-style: italic !important;
    opacity: 0.8 !important;
  }
  
  /* Timeoff event styling with transparent stripes */
  .fc-event.timeoff-event {
    background: linear-gradient(45deg, rgba(255, 152, 0, 0.3) 25%, rgba(245, 124, 0, 0.3) 25%, rgba(245, 124, 0, 0.3) 50%, rgba(255, 152, 0, 0.3) 50%, rgba(255, 152, 0, 0.3) 75%, rgba(245, 124, 0, 0.3) 75%, rgba(245, 124, 0, 0.3)) !important;
    background-size: 8px 8px !important;
    border: 1px solid rgba(230, 81, 0, 0.4) !important;
    border-radius: 4px !important;
  }
  
  .fc-toolbar {
    margin-bottom: 1.5rem !important;
    padding: 0 8px !important;
  }
  
  .fc-button {
    background-color: white !important;
    border: 2px solid #e0e0e0 !important;
    border-radius: 4px !important;
    color: #424242 !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    text-transform: none !important;
    padding: 12px 20px !important;
    margin: 0 2px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.2s ease-in-out !important;
    min-height: 48px !important;
    min-width: 48px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .fc-button:hover {
    background-color: white !important;
    border-color: #e0e0e0 !important;
    color: #424242 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transform: none !important;
  }
  
  .fc-button:focus {
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.2) !important;
    outline: none !important;
  }
  
  .fc-button:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    transform: none !important;
  }
  
  .fc-button-group {
    display: flex !important;
    border-radius: 4px !important;
    overflow: hidden !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  .fc-button-group .fc-button {
    margin: 0 !important;
    border-radius: 0 !important;
    border-right: 1px solid #e0e0e0 !important;
    box-shadow: none !important;
  }
  
  .fc-button-group .fc-button:first-child {
    border-top-left-radius: 4px !important;
    border-bottom-left-radius: 4px !important;
  }
  
  .fc-button-group .fc-button:last-child {
    border-top-right-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
    border-right: none !important;
  }
  
  .fc-button-active {
    background-color: #1976d2 !important;
    border-color: #1976d2 !important;
    color: white !important;
  }
  
  .fc-button-active:hover {
    background-color: #1976d2 !important;
    border-color: #1976d2 !important;
    color: white !important;
  }
  
  .fc-today-button {
    background-color: #1976d2 !important;
    color: white !important;
    border-color: #1976d2 !important;
    font-weight: 600 !important;
    padding: 12px 24px !important;
    border-radius: 4px !important;
  }
  
  .fc-today-button:hover {
    background-color: #1976d2 !important;
    border-color: #1976d2 !important;
    color: white !important;
  }
  
  .fc-prev-button,
  .fc-next-button {
    background-color: transparent !important;
    border: none !important;
    color: #1976d2 !important;
  }
  
  .fc-prev-button:hover,
  .fc-next-button:hover {
    background-color: rgba(25, 118, 210, 0.04) !important;
    border: none !important;
    color: #1976d2 !important;
  }
  
  .fc-prev-button:focus,
  .fc-next-button:focus {
    background-color: #1976d2 !important;
    border: none !important;
    color: white !important;
    outline: none !important;
  }
  
  .fc-prev-button:active,
  .fc-next-button:active {
    background-color: #1565c0 !important;
    border: none !important;
    color: white !important;
  }
  
  .fc-toolbar-title {
    font-size: 1.5rem !important;
    font-weight: 700 !important;
    color: #212121 !important;
    margin: 0 !important;
  }
  
  /* Desktop layout - ensure today button is inline with navigation arrows */
  @media (min-width: 769px) {
    .fc-toolbar-chunk:first-child {
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
    }
  }
  
  /* Mobile-specific improvements */
  @media (max-width: 768px) {
    .fc-toolbar {
      padding: 0 4px !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 8px !important;
    }
    
    .fc-toolbar-chunk {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 4px !important;
    }
    
    .fc-toolbar-chunk:first-child {
      order: 2 !important;
    }
    
    .fc-toolbar-chunk:nth-child(2) {
      order: 1 !important;
    }
    
    .fc-toolbar-chunk:last-child {
      order: 3 !important;
    }
    
    .fc-button {
      font-size: 12px !important;
      padding: 10px 16px !important;
      min-height: 44px !important;
      min-width: 44px !important;
    }
    
    .fc-today-button {
      padding: 10px 20px !important;
      font-size: 12px !important;
    }
    
    .fc-toolbar-title {
      font-size: 1.25rem !important;
      text-align: center !important;
      order: 1 !important;
    }
    
    .fc-prev-button, .fc-next-button {
      min-width: 44px !important;
      padding: 10px !important;
    }
  }
`;

const CalendarPage: React.FC = () => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [timeoffs, setTimeoffs] = useState<Timeoff[]>([]);
  const [hairdressers, setHairdressers] = useState<Hairdresser[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [serviceTemplates, setServiceTemplates] = useState<ServiceTemplate[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [contextMenuAppointment, setContextMenuAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState<AppointmentFormData>({
    client_id: 0,
    hairdresser_id: 0, // Will be set to first hairdresser when hairdressers are loaded
    date: "",
    status: AppointmentStatus.NEW,
    notes: "",
    services: [],
  });
  const calendarRef = useRef<FullCalendar>(null);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);

  // Client creation state
  const [openClientModal, setOpenClientModal] = useState(false);
  const [clientFormData, setClientFormData] = useState<ClientFormData>({
    name: "",
    phone_number: "",
  });

  const {
    error: appointmentsError,
    fetchAppointments,
    createAppointment: createAppointmentApi,
    updateAppointment: updateAppointmentApi,
    deleteAppointment: deleteAppointmentApi,
  } = useAppointmentsApi();

  const {
    error: hairdressersError,
    fetchHairdressers,
  } = useHairdressersApi();

  const {
    error: serviceTemplatesError,
    fetchServiceTemplates,
  } = useServiceTemplatesApi();

  const {
    error: clientsError,
    fetchClients,
    createClient: createClientApi,
    updateClient: updateClientApi,
  } = useClientsApi();

  const {
    error: timeoffsError,
    fetchTimeoffs,
  } = useTimeoffsApi();

  const loadData = async (dateFrom?: Date, dateTo?: Date): Promise<void> => {
    try {
      // Build query params for appointments - use date range if provided, otherwise use a wide range
      const appointmentParams: any = {
        limit: 0, // 0 means unlimited - get all appointments
      };
      
      if (dateFrom && dateTo) {
        // Add buffer to ensure we get all appointments (1 day before and after)
        const bufferStart = new Date(dateFrom);
        bufferStart.setDate(bufferStart.getDate() - 1);
        const bufferEnd = new Date(dateTo);
        bufferEnd.setDate(bufferEnd.getDate() + 1);
        
        appointmentParams.dateFrom = bufferStart.toISOString().split('T')[0];
        appointmentParams.dateTo = bufferEnd.toISOString().split('T')[0];
      } else {
        // Default: fetch appointments for the next 3 months if no date range is set
        const defaultStart = new Date();
        defaultStart.setMonth(defaultStart.getMonth() - 1);
        const defaultEnd = new Date();
        defaultEnd.setMonth(defaultEnd.getMonth() + 3);
        
        appointmentParams.dateFrom = defaultStart.toISOString().split('T')[0];
        appointmentParams.dateTo = defaultEnd.toISOString().split('T')[0];
      }

      const [appointmentsResponse, hairdressersData, serviceTemplatesResponse, clientsResponse, timeoffsData] = await Promise.all([
        fetchAppointments(appointmentParams),
        fetchHairdressers(),
        fetchServiceTemplates({ limit: 100 }), // Load more for calendar
        fetchClients({ limit: 10000 }), // Load all clients for calendar
        fetchTimeoffs(),
      ]);
      setAppointments(appointmentsResponse.data);
      setHairdressers(hairdressersData);
      setServiceTemplates(serviceTemplatesResponse.data);
      setClients(clientsResponse.data);
      setTimeoffs(timeoffsData);
    } catch (err) {
      // Errors are handled by the hooks
    }
  };

  // Use all appointments (no filtering)
  const filteredAppointments = useMemo(() => {
    return appointments;
  }, [appointments]);

  // Convert appointments and timeoffs to FullCalendar events
  const calendarEvents: EventInput[] = useMemo(() => {
    // Get color based on appointment status
    const getEventColor = (status: AppointmentStatus): string => {
      const colors = {
        [AppointmentStatus.NEW]: '#1976d2', // Primary blue
        [AppointmentStatus.CONFIRMED]: '#9c27b0', // Purple
        [AppointmentStatus.COMPLETED]: '#2e7d32', // Success green
        [AppointmentStatus.CANCELED]: '#d32f2f', // Error red
      };
      return colors[status] || '#1976d2';
    };

    // Map appointments to events
    const appointmentEvents = filteredAppointments.map(appointment => {
      const startDate = new Date(appointment.date);
      const duration = appointment.services?.reduce((total, service) => total + service.duration, 0) || 60; // Default 60 minutes
      const endDate = new Date(startDate.getTime() + duration * 60000);

      return {
        id: `appointment-${appointment.id}`,
        title: `${appointment.client?.name || t('calendar.unknownClient')} - ${appointment.hairdresser?.name || t('calendar.unknownHairdresser')}`,
        start: startDate,
        end: endDate,
        backgroundColor: getEventColor(appointment.status),
        borderColor: getEventColor(appointment.status),
        textColor: 'white',
        extendedProps: {
          appointment: appointment,
          type: 'appointment',
        },
        display: 'block',
        classNames: ['appointment-event'],
        // Disable editing for completed and canceled appointments
        editable: appointment.status !== AppointmentStatus.COMPLETED && appointment.status !== AppointmentStatus.CANCELED,
        startEditable: appointment.status !== AppointmentStatus.COMPLETED && appointment.status !== AppointmentStatus.CANCELED,
        durationEditable: appointment.status !== AppointmentStatus.COMPLETED && appointment.status !== AppointmentStatus.CANCELED,
        // Add data attributes for CSS styling
        'data-editable': (appointment.status !== AppointmentStatus.COMPLETED && appointment.status !== AppointmentStatus.CANCELED).toString(),
        'data-duration-editable': (appointment.status !== AppointmentStatus.COMPLETED && appointment.status !== AppointmentStatus.CANCELED).toString(),
      };
    });

    // Map timeoffs to events
    const timeoffEvents = timeoffs.map(timeoff => {
      return {
        id: `timeoff-${timeoff.id}`,
        title: `🚫 ${t('common.timeOff')}: ${timeoff.hairdresser?.name || t('common.notAvailable')}`,
        start: new Date(timeoff.startDate),
        end: new Date(timeoff.endDate),
        backgroundColor: 'rgba(255, 152, 0, 0.3)',
        borderColor: 'rgba(230, 81, 0, 0.4)',
        textColor: 'rgba(0, 0, 0, 0.7)',
        extendedProps: {
          timeoff: timeoff,
          type: 'timeoff',
        },
        display: 'block',
        classNames: ['timeoff-event'],
        editable: false,
        startEditable: false,
        durationEditable: false,
      };
    });

    return [...appointmentEvents, ...timeoffEvents];
  }, [filteredAppointments, timeoffs, t]);

  // Handle event click
  const handleEventClick = (clickInfo: any) => {
    const eventType = clickInfo.event.extendedProps.type;
    
    // Only handle appointment clicks, ignore timeoff clicks
    if (eventType === 'appointment') {
      const appointment = clickInfo.event.extendedProps.appointment;
      setSelectedAppointment(appointment);
      setOpenInfoDialog(true);
    }
    // Timeoff events are not clickable (they're just visual indicators)
  };

  // Handle event drag (moving appointment to different time/date)
  const handleEventDrop = async (dropInfo: any) => {
    console.log('Event drop triggered:', dropInfo);
    const appointment = dropInfo.event.extendedProps.appointment;
    const newStartDate = dropInfo.event.start;
    const newEndDate = dropInfo.event.end;
    
    console.log('Appointment:', appointment);
    console.log('New start date:', newStartDate);
    console.log('New end date:', newEndDate);
    
    try {
      // Calculate new duration based on the dragged end time
      const newDurationMinutes = Math.round((newEndDate.getTime() - newStartDate.getTime()) / (1000 * 60));
      
      console.log('New duration minutes:', newDurationMinutes);
      
      // Update the appointment with new date and duration
      const updateData: AppointmentFormData = {
        client_id: appointment.client?.id || 0,
        hairdresser_id: appointment.hairdresser?.id || 0,
        date: newStartDate.toISOString(),
        status: appointment.status,
        notes: appointment.notes || "",
        services: appointment.services?.map((service: Service) => ({
          name: service.name,
          description: service.description || "",
          price: Math.round(parseFloat(service.price)),
          duration: service.duration, // Keep individual service durations
        })) || [],
      };
      
      console.log('Update data:', updateData);
      
      await updateAppointmentApi(appointment.id, updateData);
      await loadData();
      toast.success(t('appointments.appointmentMoved'));
    } catch (err) {
      console.error('Error moving appointment:', err);
      // Revert the event position on error
      dropInfo.revert();
      toast.error(t('appointments.failedToMoveAppointment'));
    }
  };

  // Handle event resize (changing appointment duration or start time)
  const handleEventResize = async (resizeInfo: any) => {
    const appointment = resizeInfo.event.extendedProps.appointment;
    const newStartDate = resizeInfo.event.start;
    const newEndDate = resizeInfo.event.end;
    const originalStartDate = new Date(appointment.date);
    const originalEndDate = new Date(originalStartDate.getTime() + (appointment.services?.reduce((total: number, service: Service) => total + service.duration, 0) || 60) * 60000);
    
    try {
      // Get original services first
      const originalServices = appointment.services || [];
      
      // Check if start time changed (dragging from top) or end time changed (dragging from bottom)
      const startTimeChanged = Math.abs(newStartDate.getTime() - originalStartDate.getTime()) > 60000; // More than 1 minute difference
      const endTimeChanged = Math.abs(newEndDate.getTime() - originalEndDate.getTime()) > 60000; // More than 1 minute difference
      
      console.log('Start time changed:', startTimeChanged, 'End time changed:', endTimeChanged);
      console.log('Original start:', originalStartDate, 'New start:', newStartDate);
      console.log('Original end:', originalEndDate, 'New end:', newEndDate);
      
      if (startTimeChanged && !endTimeChanged) {
        // START TIME CHANGE: Update start time and adjust service durations to maintain end time
        console.log('Updating start time while maintaining end time');
        
        // Calculate new total duration (from new start to original end)
        const newTotalDuration = Math.round((originalEndDate.getTime() - newStartDate.getTime()) / (1000 * 60));
        const originalTotalDuration = originalServices.reduce((total: number, service: Service) => total + service.duration, 0);
        const durationDifference = newTotalDuration - originalTotalDuration;
        
        console.log('New total duration:', newTotalDuration, 'minutes');
        console.log('Duration difference:', durationDifference, 'minutes');
        
        let updatedServices: AppointmentServiceFormData[];
        
        if (durationDifference > 0) {
          // EXTENDING: Add time to the shortest service
          const sortedServices = [...originalServices].sort((a, b) => a.duration - b.duration);
          const shortestService = sortedServices[0];
          
          updatedServices = originalServices.map((service: Service) => {
            if (service.id === shortestService.id) {
              return {
                name: service.name,
                description: service.description || "",
                price: Math.round(parseFloat(service.price)),
                duration: service.duration + durationDifference,
              };
            } else {
              return {
                name: service.name,
                description: service.description || "",
                price: Math.round(parseFloat(service.price)),
                duration: service.duration,
              };
            }
          });
          
          console.log('Extended shortest service:', shortestService.name, 'by', durationDifference, 'minutes');
        } else if (durationDifference < 0) {
          // DECREASING: Remove time from the longest service
          const sortedServices = [...originalServices].sort((a, b) => b.duration - a.duration);
          const longestService = sortedServices[0];
          
          updatedServices = originalServices.map((service: Service) => {
            if (service.id === longestService.id) {
              const newDuration = Math.max(5, service.duration + durationDifference);
              return {
                name: service.name,
                description: service.description || "",
                price: Math.round(parseFloat(service.price)),
                duration: newDuration,
              };
            } else {
              return {
                name: service.name,
                description: service.description || "",
                price: Math.round(parseFloat(service.price)),
                duration: service.duration,
              };
            }
          });
          
          console.log('Decreased longest service:', longestService.name, 'by', Math.abs(durationDifference), 'minutes');
        } else {
          // No duration change needed
          updatedServices = originalServices.map((service: Service) => ({
            name: service.name,
            description: service.description || "",
            price: Math.round(parseFloat(service.price)),
            duration: service.duration,
          }));
        }
        
        const updateData: AppointmentFormData = {
          client_id: appointment.client?.id || 0,
          hairdresser_id: appointment.hairdresser?.id || 0,
          date: newStartDate.toISOString(),
          status: appointment.status,
          notes: appointment.notes || "",
          services: updatedServices,
        };
        
        await updateAppointmentApi(appointment.id, updateData);
        await loadData();
        toast.success(t('appointments.appointmentStartTimeUpdated'));
        return;
      }
      
      // DURATION CHANGE: Handle service duration adjustments
      const newDurationMinutes = Math.round((newEndDate.getTime() - newStartDate.getTime()) / (1000 * 60));
      
      // Calculate duration difference
      const originalTotalDuration = originalServices.reduce((total: number, service: Service) => total + service.duration, 0);
      
      // Calculate duration difference
      const durationDifference = newDurationMinutes - originalTotalDuration;
      
      console.log('Duration change:', durationDifference, 'minutes');
      console.log('Original services:', originalServices.map((s: Service) => ({ name: s.name, duration: s.duration })));
      
      let updatedServices: AppointmentServiceFormData[];
      
      if (durationDifference > 0) {
        // EXTENDING: Add time to the shortest service
        const sortedServices = [...originalServices].sort((a, b) => a.duration - b.duration);
        const shortestService = sortedServices[0];
        
        updatedServices = originalServices.map((service: Service) => {
          if (service.id === shortestService.id) {
            // Extend the shortest service
            return {
              name: service.name,
              description: service.description || "",
              price: Math.round(parseFloat(service.price)),
              duration: service.duration + durationDifference,
            };
          } else {
            // Keep other services unchanged
            return {
              name: service.name,
              description: service.description || "",
              price: Math.round(parseFloat(service.price)),
              duration: service.duration,
            };
          }
        });
        
        console.log('Extended shortest service:', shortestService.name, 'by', durationDifference, 'minutes');
      } else if (durationDifference < 0) {
        // DECREASING: Remove time from the longest service
        const sortedServices = [...originalServices].sort((a, b) => b.duration - a.duration);
        const longestService = sortedServices[0];
        
        updatedServices = originalServices.map((service: Service) => {
          if (service.id === longestService.id) {
            // Decrease the longest service (but don't go below 5 minutes minimum)
            const newDuration = Math.max(5, service.duration + durationDifference);
            return {
              name: service.name,
              description: service.description || "",
              price: Math.round(parseFloat(service.price)),
              duration: newDuration,
            };
          } else {
            // Keep other services unchanged
            return {
              name: service.name,
              description: service.description || "",
              price: Math.round(parseFloat(service.price)),
              duration: service.duration,
            };
          }
        });
        
        console.log('Decreased longest service:', longestService.name, 'by', Math.abs(durationDifference), 'minutes');
      } else {
        // No change needed
        updatedServices = originalServices.map((service: Service) => ({
          name: service.name,
          description: service.description || "",
          price: Math.round(parseFloat(service.price)),
          duration: service.duration,
        }));
      }
      
      console.log('Updated services:', updatedServices.map((s: AppointmentServiceFormData) => ({ name: s.name, duration: s.duration })));
      
      // Update the appointment with new service durations and potentially new start time
      const updateData: AppointmentFormData = {
        client_id: appointment.client?.id || 0,
        hairdresser_id: appointment.hairdresser?.id || 0,
        date: newStartDate.toISOString(), // Use new start time
        status: appointment.status,
        notes: appointment.notes || "",
        services: updatedServices,
      };
      
      await updateAppointmentApi(appointment.id, updateData);
      await loadData();
      toast.success(t('appointments.appointmentDurationUpdated'));
    } catch (err) {
      console.error('Error resizing appointment:', err);
      // Revert the event size on error
      resizeInfo.revert();
      toast.error(t('appointments.failedToUpdateDuration'));
    }
  };


  // Handle context menu close
  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuAppointment(null);
  };

  // Handle context menu (right-click)
  const handleContextMenu = (event: React.MouseEvent, appointment: Appointment) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenuAppointment(appointment);
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };


  // Handle quick status change
  const handleQuickStatusChange = async (appointment: Appointment, newStatus: AppointmentStatus) => {
    try {
      // Create a minimal form data object with just the status change
      const statusUpdateData: AppointmentFormData = {
        client_id: appointment.client?.id || 0,
        hairdresser_id: appointment.hairdresser?.id || 0,
        date: appointment.date.toString(),
        status: newStatus,
        notes: appointment.notes || "",
        services: appointment.services?.map(service => ({
          name: service.name,
          description: service.description || "",
          price: Math.round(parseFloat(service.price)),
          duration: service.duration,
        })) || [],
      };
      
      await updateAppointmentApi(appointment.id, statusUpdateData);
      await loadData();
      toast.success(t('appointments.statusUpdated', { status: newStatus.toLowerCase() }));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Helper function to set minutes to 0
  const setMinutesToZero = (date: Date): string => {
    const dateWithZeroMinutes = new Date(date);
    dateWithZeroMinutes.setMinutes(0, 0, 0); // Set minutes, seconds, and milliseconds to 0
    return dateWithZeroMinutes.toISOString();
  };

  // Handle date click (for adding new appointments)
  const handleDateClick = (dateClickInfo: any) => {
    const clickedDate = dateClickInfo.date;
    const dateStr = setMinutesToZero(clickedDate);
    
    // Pre-fill form with selected date and time (minutes set to 0)
    setFormData({
      client_id: 0,
      hairdresser_id: hairdressers.length > 0 ? hairdressers[0].id : 0,
      date: dateStr,
      status: AppointmentStatus.NEW,
      notes: "",
      services: [],
    });
    
    // Open create appointment modal
    setOpenCreateModal(true);
  };

  // Custom event content renderer
  const renderEventContent = (eventInfo: any) => {
    const calendar = calendarRef.current?.getApi();
    const view = calendar?.view.type;
    const eventType = eventInfo.event.extendedProps.type;
    const appointment = eventInfo.event.extendedProps.appointment;
    const timeoff = eventInfo.event.extendedProps.timeoff;
    
    // Handle timeoff events
    if (eventType === 'timeoff') {
      if (view === 'dayGridMonth') {
        return (
          <div style={{ padding: '2px', fontSize: '12px', fontWeight: 'bold' }}>
            🚫 {timeoff?.hairdresser?.name || t('common.notAvailable')}
          </div>
        );
      }
      
      if (view === 'timeGridWeek' || view === 'timeGridDay') {
        return (
          <div style={{ padding: '2px', fontSize: '11px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '1px' }}>
              🚫 {t('common.timeOff')}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              {timeoff?.hairdresser?.name || t('common.notAvailable')}
            </div>
            {
              timeoff?.description && (
                <div style={{ fontSize: '12px', opacity: 0.8, fontStyle: 'italic' }}>
                  {timeoff?.description}
                </div>
              )
            }
          </div>
        );
      }
      
      return null;
    }
    
    // Handle appointment events
    const isEditable = appointment?.status !== AppointmentStatus.COMPLETED && appointment?.status !== AppointmentStatus.CANCELED;
    
    // In month view, show only client name
    if (view === 'dayGridMonth') {
      return (
        <div 
          style={{ padding: '2px', fontSize: '12px', fontWeight: 'bold' }}
          onContextMenu={(e) => handleContextMenu(e, appointment)}
        >
          {appointment?.client?.name || t('calendar.unknownClient')}
        </div>
      );
    }
    
    // In week/day view, show client name with start and finish time
    if (view === 'timeGridWeek' || view === 'timeGridDay') {
      const startTime = eventInfo.event.start;
      const endTime = eventInfo.event.end;
      
      const startTimeString = startTime.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      const endTimeString = endTime.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      return (
        <div 
          style={{ padding: '2px', fontSize: '12px' }}
          onContextMenu={(e) => handleContextMenu(e, appointment)}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '1px' }}>
            {startTimeString} - {endTimeString}
          </div>
          <div>
            {appointment?.client?.name || t('calendar.unknownClient')}
          </div>
          {appointment?.services && appointment.services.length > 0 && (
            <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9, fontStyle: 'italic' }}>
              {appointment.services.map((service: any) => service.name).join(', ').length > 35 
                ? appointment.services.map((service: any) => service.name).join(', ').substring(0, 35) + '...'
                : appointment.services.map((service: any) => service.name).join(', ')
              }
            </div>
          )}
        </div>
      );
    }
    
    // Fallback for other views
    return (
      <div 
        style={{ padding: '2px', fontSize: '12px' }}
        onContextMenu={(e) => handleContextMenu(e, appointment)}
      >
        {appointment?.client?.name || 'Unknown Client'}
      </div>
    );
  };

  // Handle edit appointment
  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      client_id: appointment.client?.id || 0,
      hairdresser_id: appointment.hairdresser?.id || 0,
      date: appointment.date.toString(),
      status: appointment.status,
      notes: appointment.notes || "",
      services: appointment.services?.map(service => ({
        name: service.name,
        description: service.description || "",
        price: Math.round(parseFloat(service.price)),
        duration: service.duration,
      })) || [],
    });
    
    // Navigate main calendar to the appointment's date and scroll to the time with 15-minute margin
    const appointmentDate = new Date(appointment.date);
    
    setTimeout(() => {
      const calendar = calendarRef.current?.getApi();
      if (calendar) {
        // Navigate to the appointment's date
        calendar.gotoDate(appointmentDate);
        
        // Scroll to the appointment time with 30-minute margin
        const hours = appointmentDate.getHours();
        const minutes = appointmentDate.getMinutes();
        
        // Calculate scroll time: 30 minutes before appointment
        let scrollHours = hours;
        let scrollMinutes = minutes - 30; // 30-minute margin
        
        // Handle minute underflow
        if (scrollMinutes < 0) {
          scrollMinutes += 60;
          scrollHours -= 1;
        }
        
        // Ensure we don't go below 8 AM (calendar start time)
        if (scrollHours < 8) {
          scrollHours = 8;
          scrollMinutes = 0;
        }
        
        const timeStr = `${scrollHours.toString().padStart(2, '0')}:${scrollMinutes.toString().padStart(2, '0')}`;
        calendar.scrollToTime(timeStr);
      }
    }, 100); // Small delay to ensure calendar is ready
    
    setOpenEditModal(true);
    setOpenInfoDialog(false);
  };

  // Handle delete appointment
  const handleDeleteAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setOpenDeleteDialog(true);
    setOpenInfoDialog(false);
  };

  // Update appointment
  const updateAppointment = async (changedData?: Partial<AppointmentFormData> | any): Promise<void> => {
    if (!selectedAppointment) return;

    try {
      // If this is a finalized appointment (has payments), just refresh the data
      if (changedData && changedData.payments && changedData.payments.length > 0) {
        await loadData();
        setOpenEditModal(false);
        setSelectedAppointment(null);
        resetForm();
        return;
      }

      // Use changed data if provided, otherwise use all form data
      const dataToUpdate = changedData || formData;
      await updateAppointmentApi(selectedAppointment.id, dataToUpdate);
      await loadData();
      setOpenEditModal(false);
      setSelectedAppointment(null);
      resetForm();
      toast.success(t('appointments.appointmentUpdated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Delete appointment
  const deleteAppointment = async (): Promise<void> => {
    if (!selectedAppointment) return;

    try {
      await deleteAppointmentApi(selectedAppointment.id);
      await loadData();
      setOpenDeleteDialog(false);
      setSelectedAppointment(null);
      toast.success(t('appointments.appointmentDeleted'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Reset form
  const resetForm = (): void => {
    setFormData({
      client_id: 0,
      hairdresser_id: hairdressers.length > 0 ? hairdressers[0].id : 0,
      date: "",
      status: AppointmentStatus.NEW,
      notes: "",
      services: [],
    });
  };

  // Handle input change
  const handleInputChange =
    (field: keyof AppointmentFormData) =>
    (event: any): void => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // Handle service change
  const handleServiceChange = (index: number, field: keyof AppointmentServiceFormData, value: any): void => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === index ? { ...service, [field]: value } : service
      ),
    }));
  };

  // Handle add service
  const handleAddService = (serviceTemplate: ServiceTemplate): void => {
    const newService: AppointmentServiceFormData = {
      name: serviceTemplate.name,
      description: serviceTemplate.description || "",
      price: Math.round(parseFloat(serviceTemplate.price)),
      duration: serviceTemplate.estimated_duration,
    };
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  // Handle remove service
  const handleRemoveService = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  // Handle modal close
  const handleModalClose = (): void => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    resetForm();
    // Refresh appointments to get updated data (including payments for finalized appointments)
    if (dateRange) {
      loadData(dateRange.start, dateRange.end);
    } else {
      loadData();
    }
  };

  const handleClientModalClose = (): void => {
    setOpenClientModal(false);
    setClientFormData({ name: "", phone_number: "" });
  };

  // Client creation handlers
  const handleClientInputChange = (field: keyof ClientFormData) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    setClientFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const createClient = async (data?: ClientFormData | Partial<ClientFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to client form data
      const dataToCreate = data || clientFormData;
      const newClient = await createClientApi(dataToCreate as ClientFormData);
      // Refresh clients list
      const clientsResponse = await fetchClients({ limit: 10000 });
      setClients(clientsResponse.data);
      
      // Set the newly created client as selected
      setFormData(prev => ({
        ...prev,
        client_id: newClient.id
      }));
      
      setOpenClientModal(false);
      setClientFormData({ name: "", phone_number: "" });
      toast.success(t('clients.clientCreated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCreateClient = (searchText?: string): void => {
    // Pre-fill the name field with the search text if provided
    if (searchText && searchText.trim()) {
      setClientFormData({ name: searchText.trim(), phone_number: "" });
    } else {
      setClientFormData({ name: "", phone_number: "" });
    }
    setOpenClientModal(true);
  };

  // Wrapper function to update client and refresh clients data
  const handleUpdateClient = async (id: number, data: Partial<ClientFormData>): Promise<Client> => {
    const updatedClient = await updateClientApi(id, data);
    // Refresh clients data after update
    const clientsResponse = await fetchClients({ limit: 10000 });
    setClients(clientsResponse.data);
    return updatedClient;
  };

  // Create appointment
  const createAppointment = async (data?: AppointmentFormData | Partial<AppointmentFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to form data
      const dataToCreate = data || formData;
      await createAppointmentApi(dataToCreate as AppointmentFormData);
      await loadData();
      setOpenCreateModal(false);
      resetForm();
      toast.success(t('appointments.appointmentCreated'));
    } catch (err) {
      // Error is handled by the hook
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await loadData();
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, []);

  // Set default hairdresser when hairdressers are loaded
  useEffect(() => {
    if (hairdressers.length > 0 && formData.hairdresser_id === 0) {
      setFormData(prev => ({
        ...prev,
        hairdresser_id: hairdressers[0].id
      }));
    }
  }, [hairdressers, formData.hairdresser_id]);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <style>{calendarHeaderStyles}</style>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          gutterBottom
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 0
          }}
        >
          {t('calendar.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
          onClick={() => {
            // Set current date/time with minutes set to 0
            const now = new Date();
            const dateStr = setMinutesToZero(now);
            
            setFormData({
              client_id: 0,
              hairdresser_id: hairdressers.length > 0 ? hairdressers[0].id : 0,
              date: dateStr,
              status: AppointmentStatus.NEW,
              notes: "",
              services: [],
            });
            
            setOpenCreateModal(true);
          }}
        >
          {t('calendar.addAppointment')}
        </Button>
      </Box>

      {(appointmentsError || hairdressersError || serviceTemplatesError || clientsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {appointmentsError?.message || hairdressersError?.message || serviceTemplatesError?.message || clientsError?.message}
        </Alert>
      )}

      <Paper 
        elevation={2}
        sx={{ 
          height: isMobile ? 600 : 700, 
          p: isMobile ? 1 : 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          '& .fc': {
            height: '100%',
          }
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          locale={plLocale}
          buttonText={{
            today: t('calendar.today'),
            month: t('calendar.month'),
            week: t('calendar.week'),
            day: t('calendar.day')
          }}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          dateClick={handleDateClick}
          eventContent={renderEventContent}
          height="100%"
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          slotDuration="00:15:00"
          slotLabelInterval="01:00:00"
          nowIndicator={true}
          eventDisplay="block"
          allDaySlot={false}
          firstDay={1}
          dayMaxEvents={3}
          moreLinkClick="popover"
          editable={true}
          eventResizableFromStart={true}
          eventDurationEditable={true}
          eventStartEditable={true}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          titleFormat={{
            year: 'numeric',
            month: 'long'
          }}
          dayHeaderFormat={{
            weekday: 'short',
            day: 'numeric'
          }}
          datesSet={(dateInfo) => {
            // Update date range when calendar view changes
            const start = dateInfo.start;
            const end = dateInfo.end;
            setDateRange({ start, end });
            // Fetch appointments for the visible date range
            loadData(start, end);
          }}
        />
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#1976d2', borderRadius: 1 }} />
          <Typography variant="body2">{t('calendar.new')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#9c27b0', borderRadius: 1 }} />
          <Typography variant="body2">{t('calendar.confirmed')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#2e7d32', borderRadius: 1 }} />
          <Typography variant="body2">{t('calendar.completed')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#d32f2f', borderRadius: 1 }} />
          <Typography variant="body2">{t('calendar.canceled')}</Typography>
        </Box>
      </Box>

      <AppointmentInfoDialog
        open={openInfoDialog}
        onClose={() => setOpenInfoDialog(false)}
        appointment={selectedAppointment}
        clients={clients}
        timeoffs={timeoffs}
        showActions={true}
        onEdit={handleEditAppointment}
        onDelete={handleDeleteAppointment}
      />

      <AppointmentFormModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSubmit={createAppointment}
        formData={formData}
        onInputChange={handleInputChange}
        onServiceChange={handleServiceChange}
        onAddService={handleAddService}
        onRemoveService={handleRemoveService}
        onCreateClient={handleCreateClient}
        onUpdateClient={handleUpdateClient}
        onRefreshAppointments={loadData}
        title={t('appointments.addNewAppointment')}
        submitButtonText={t('common.create')}
        clients={clients}
        hairdressers={hairdressers}
        serviceTemplates={serviceTemplates}
        timeoffs={timeoffs}
        appointments={appointments}
      />

      <AppointmentFormModal
        open={openEditModal}
        onClose={handleModalClose}
        onSubmit={updateAppointment}
        formData={formData}
        onInputChange={handleInputChange}
        onServiceChange={handleServiceChange}
        onAddService={handleAddService}
        onRemoveService={handleRemoveService}
        onCreateClient={handleCreateClient}
        onUpdateClient={handleUpdateClient}
        onRefreshAppointments={loadData}
        title={t('appointments.editAppointment')}
        submitButtonText={t('common.save')}
        clients={clients}
        hairdressers={hairdressers}
        serviceTemplates={serviceTemplates}
        timeoffs={timeoffs}
        appointments={appointments}
        isEdit={true}
        editingAppointment={selectedAppointment}
      />

      <AppointmentDeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deleteAppointment}
        appointment={selectedAppointment}
      />

      <AppointmentContextMenu
        open={contextMenu !== null}
        anchorEl={contextMenu ? {
          getBoundingClientRect: () => ({
            top: contextMenu.mouseY,
            left: contextMenu.mouseX,
            bottom: contextMenu.mouseY,
            right: contextMenu.mouseX,
            width: 0,
            height: 0,
          }),
        } as HTMLElement : null}
        onClose={handleContextMenuClose}
        appointment={contextMenuAppointment}
        onEdit={handleEditAppointment}
        onDelete={handleDeleteAppointment}
        onStatusChange={handleQuickStatusChange}
      />

      <ClientFormModal
        open={openClientModal}
        onClose={handleClientModalClose}
        onSubmit={createClient}
        formData={clientFormData}
        onInputChange={handleClientInputChange}
        title={t('clients.addNewClient')}
        submitButtonText={t('common.create')}
      />
    </Container>
  );
};

export default CalendarPage;